<template>
  <form
    v-if="showForm"
    id="articleForm"
    action
    method="POST"
  >
    <h1
      class="title is-3"
      v-t="'title_' + formMode"
    />

    <b-field>
      <b-checkbox
        true-value="1"
        false-value="0"
        size="is-medium"
        type="is-danger"
        v-model="formData.advanced"
      >
        <span v-t="'advanced_article'" />
      </b-checkbox>
    </b-field>

    <b-field
      :label="$t('fields.labels.title')"
      :type="{ 'is-danger': errors.title }"
      :message="errors.title ? errors.title[0] : ''"
      :custom-class="errors.title ? 'has-text-danger' : ''"
    >
      <b-input
        type="text"
        :placeholder="$t('fields.placeholders.title')"
        :value="formData.title"
        v-model="formData.title"
        size="is-large"
        required
      />
    </b-field>

    <b-field
      :label="$t('fields.labels.description')"
      :type="{ 'is-danger': errors.description }"
      :message="errors.description ? errors.description[0] : ''"
      :custom-class="errors.description ? 'has-text-danger' : ''"
    >
      <b-input
        type="text"
        :placeholder="$t('fields.placeholders.description')"
        :value="formData.description"
        v-model="formData.description"
        size="is-medium"
        required
      />
    </b-field>

    <b-field
      :label="$t('fields.labels.content')"
      :type="{ 'is-danger': errors.content }"
      :message="errors.content ? errors.content[0] : ''"
      :custom-class="errors.content ? 'has-text-danger' : ''"
    >
      <rich-editor
        :content.sync="formData.content"
        :placeholder="$t('fields.placeholders.content')"
        @change="contentChange"
      />
    </b-field>

    <b-field position="is-right">
      <p class="control">
        <button
          type="submit"
          class="button is-danger article-form__action"
          @click.prevent="save()"
        >
          <span v-t="'save'" />
        </button>
      </p>
      <p class="control has-margin-left-sm">
        <button
          type="button"
          class="button is-light article-form__action article-form__cancel"
          @click="cancel()"
        >
          <span v-t="'cancel'" />
        </button>
      </p>
    </b-field>
  </form>
  <loading-content v-else />
</template>

<script>
import API from '@/api'
import { mapMutations } from 'vuex'
import RichEditor from './RichEditor'
import LoadingContent from './LoadingContent'

export default {
  name: 'ArticleForm',
  props: { product: { type: Object, required: true } },
  components: {
    RichEditor,
    LoadingContent
  },
  data () {
    return {
      isLoading: true,
      errors: {},
      // O link "Editar" usa article.id na rota (nao o slug), entao
      // `articleId` (vindo de $route.params.article) e o ID numerico. Para
      // voltar a visualizacao do artigo ao cancelar, a rota "article"
      // precisa do slug — guardamos ele aqui assim que o artigo carrega.
      articleSlug: null,
      formData: {
        product_id: this.product.id,
        title: '',
        description: '',
        content: '',
        advanced: false,
        attachs: []
      }
    }
  },

  mounted () {
    if (this.formMode === 'edit') {
      this.fetchFormData()
    } else {
      this.isLoading = false
    }
  },

  methods: {
    fetchFormData () {
      this.isLoading = true
      API.articles.find(this.articleId, article => {
        this.isLoading = false
        this.articleSlug = article.slug
        this.formData = {
          product_id: article.product.id,
          title: article.title,
          description: article.description,
          content: article.content,
          advanced: article.advanced ? '1' : '0'
        }
      })
    },

    contentChange ({ content, attachs }) {
      this.formData.content = content
      this.formData.attachs = attachs
    },

    save () {
      if (this.formMode === 'new') {
        API.articles.store(
          this.formData,
          article => {
            this.saveCallback(article)
            this.addArticleToIndex(article)
          },
          this.errorCallback
        )
      } else {
        API.articles.update(this.articleId, this.formData, this.saveCallback, this.errorCallback)
      }
    },

    saveCallback (article) {
      this.$snotify.success(this.$t('alerts.' + this.formMode), this.$t('alert.title.success'))
      this.$router.push({ name: 'article', params: { article: article.slug } })
      window.scrollTo(0, 0)
    },

    // Sem artigo salvo para voltar (modo "new"), cancelar leva pra lista do
    // produto. Editando um artigo existente, volta pra visualizacao dele,
    // descartando as alteracoes nao salvas.
    cancel () {
      if (this.formMode === 'new') {
        this.$router.push({ name: 'manual', params: { product: this.product.slug } })
      } else {
        this.$router.push({ name: 'article', params: { article: this.articleSlug } })
      }
    },

    errorCallback (err) {
      this.$snotify.error(this.$t('alerts.error'), this.$t('alert.title.error'))
      if (err.response) {
        this.errors = err.response.data.errors
      }
    },

    ...mapMutations({ addArticleToIndex: 'article/addArticle' })
  },

  computed: {
    articleId () {
      return this.$route.params.article
    },

    showForm () {
      return this.formMode === 'new' || (this.formMode === 'edit' && !this.isLoading)
    },

    formMode () {
      return this.$route.name === 'new-article' ? 'new' : 'edit'
    }
  },

  metaInfo () {
    return { title: this.$t(this.formMode === 'new' ? 'title_new' : 'title_edit') }
  }
}
</script>

<style scoped>
/* O Buefy junta os dois controles num "field has-addons" (grupo de botoes
   conectados), que zera o border-radius do lado onde eles se encostariam —
   faz sentido quando estao colados, mas com o espaco entre eles (abaixo) o
   canto reto ficou visivel e destoando. O !important e necessario porque a
   regra do has-addons (.field.has-addons .control:first/last-child .button)
   e mais especifica que uma unica classe. */
.article-form__action {
  border-radius: 6px !important;
}

.article-form__cancel {
  /* O "is-light" do Bulma nao tem borda propria (fica confundindo com o
     fundo branco da pagina). Essa borda um pouco mais escura da um contorno
     sutil, sem chamar tanta atencao quanto o botao de salvar. */
  border-color: #c7c7c7;
}

.article-form__cancel:hover {
  border-color: #aaaaaa;
}
</style>

<i18n src="@/lang/locales.json"></i18n>
<i18n>
{
  "en-US": {
    "title_new": "Create new article",
    "title_edit": "Editing the article",
    "fields": {
      "labels": {
        "title": "Title",
        "description": "Subtitle",
        "content": "Content"
      },
      "placeholders": {
        "title": "Article title",
        "description": "A short description",
        "content": "The article content"
      }
    },
    "advanced_article": "Restricted content / Not visible to customers",
    "save": "Save informations",
    "cancel": "Cancel",
    "alerts": {
      "new": "Article created successfully!",
      "edit": "Article updated successfully!",
      "error": "An error occurred when creating the article!"
    }
  },
  "pt-BR": {
    "title_new": "Escrever novo artigo",
    "title_edit": "Editando o artigo",
    "fields": {
      "labels": {
        "title": "Título",
        "description": "Subtítulo",
        "content": "Conteúdo"
      },
      "placeholders": {
        "title": "Título do artigo",
        "description": "Uma breve descrição",
        "content": "Conteúdo do artigo"
      }
    },
    "advanced_article": "Conteúdo restrito / Não visível para clientes",
    "save": "Salvar informações",
    "cancel": "Cancelar",
    "alerts": {
      "new": "Artigo cadastrado com sucesso!",
      "edit": "Artigo atualizado com sucesso!",
      "error": "Erro ao cadastrar o artigo!"
    }
  }
}
</i18n>
